import React from 'react'
import PropTypes from 'prop-types'
import { ModalWrapper } from 'components'
import {
  AnchorPositions,
  PopupDirections,
  ModalTypes,
  optionsOfEnum
} from 'constants/enums'

export default class PopupProvider extends React.Component {
  static propTypes = {
    // Basic usage
    children: PropTypes.oneOfType([
      PropTypes.node,
      PropTypes.arrayOf(PropTypes.node)
    ]), //Contents of the popup
    renderTrigger: PropTypes.func, //function to render trigger UI. For example, if we click on a button to show the popup. The button is the trigger.
    renderChildren: PropTypes.func,
    anchorPosition: PropTypes.oneOf(optionsOfEnum(AnchorPositions)), //The popup is anchored on around the trigger, for example, on the topright of the trigger.
    popupDirection: PropTypes.oneOf(optionsOfEnum(PopupDirections)), //The popup origins from the anchor, and goes in certain directions, like downRight, left, up, etc.
    tapOutSideToDismiss: PropTypes.bool,
    offsetHorizontal: PropTypes.number,
    offsetVertical: PropTypes.number,
    // Advanced usage
    isOpen: PropTypes.bool, //When provided, it works as a controlled component
    onToggle: PropTypes.func, //call back when popup open / closes
    uniqueIdentifier: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), //passed back in onToggle.
    /**
     * Can be "absolute" or "fixed"
     * Should use "absolute" in most cases.
     * May consider "fixed" when modal is presented inside a scrollview, such that the popup won't messup the height of your scroll view.
     * If you really need to use "fixed" inside a scrollview, please implement something like scroll to dismiss in your scrollview listener since the popup is not going to move when you scroll.
     */
    modalType: PropTypes.oneOf(optionsOfEnum(ModalTypes)),
    className: PropTypes.string
  }

  static defaultProps = {
    modalType: 'absolute',
    anchorPosition: AnchorPositions.topRight,
    popupDirection: PopupDirections.downRight,
    offsetHorizontal: 0.0,
    offsetVertical: 0.0
  }

  constructor(props) {
    super(props)
    this.state = {
      isOpen: false,
      showFixedTypeModal: false
    }
  }

  handleClickOutside = () => {
    const { tapOutSideToDismiss } = this.props
    if (tapOutSideToDismiss) {
      this.setOnOff(false)
    }
  }

  notifyOnToggle = () => {
    const { onToggle, uniqueIdentifier } = this.props
    const { isOpen } = this.state
    if (onToggle) {
      onToggle({ uniqueIdentifier, isOpen })
    }
  }

  setOnOff = (value) => {
    this.setState({ isOpen: value }, this.notifyOnToggle)
  }

  getIsOpen = () => {
    const { isOpen: propIsOpen } = this.props
    const { isOpen: stateIsOpen } = this.state
    return propIsOpen !== undefined ? propIsOpen : stateIsOpen
  }

  generateStyle = () => {
    const { modalType, popupDirection, offsetHorizontal, offsetVertical } =
      this.props
    if (this.isRefValid(this.containerRef) && this.isRefValid(this.modalRef)) {
      const anchorRect = this.containerRef.getBoundingClientRect()
      const modalRect = this.modalRef.getBoundingClientRect()

      const { x, y } = anchorRect
      const offsetX =
        (modalType === ModalTypes.fixed ? x : 0) + offsetHorizontal
      const offsetY = (modalType === ModalTypes.fixed ? y : 0) + offsetVertical
      const { width, height } = modalRect
      if (popupDirection === PopupDirections.upLeft) {
        return { left: offsetX - width, top: offsetY - height }
      } else if (popupDirection === PopupDirections.up) {
        return { left: offsetX - width / 2.0, top: offsetY - height }
      } else if (popupDirection === PopupDirections.upRight) {
        return { left: offsetX, top: offsetY - height }
      } else if (popupDirection === PopupDirections.left) {
        return { left: offsetX - width, top: offsetY - height / 2.0 }
      } else if (popupDirection === PopupDirections.right) {
        return { left: offsetX, top: offsetY - height / 2.0 }
      } else if (popupDirection === PopupDirections.downLeft) {
        return { left: offsetX - width, top: offsetY }
      } else if (popupDirection === PopupDirections.down) {
        return { left: offsetX - width / 2.0, top: offsetY }
      } else if (popupDirection === PopupDirections.downRight) {
        return { left: offsetX, top: offsetY }
      } else if (popupDirection === PopupDirections.downCenter) {
        return { left: -70, top: 10 }
      } else {
        return { left: -99999, top: -99999 }
      }
    } else {
      return { left: -99999, top: -99999 }
    }
  }

  tryFlushModal = () => {
    if (this.isRefValid(this.modalRef) && this.isRefValid(this.containerRef)) {
      this.setState({ showFixedTypeModal: true })
    }
  }

  isRefValid = (ref) => {
    if (ref === undefined || ref === null) {
      return false
    } else {
      const rect = ref.getBoundingClientRect()
      const { x, y, width, height } = rect
      //TODO: This checks if refed node has been unmounted. This needs to be improved. But I am not sure how to do it at this point.
      //May be this: https://stackoverflow.com/questions/37521684/how-to-check-if-a-component-is-mounted-in-react-native-es6
      if (x === 0 && y === 0 && width === 0 && height === 0) {
        return false
      } else {
        return true
      }
    }
  }

  renderTriggerContent = () => {
    const { renderTrigger } = this.props
    if (renderTrigger) {
      return renderTrigger({ setOnOff: this.setOnOff })
    } else {
      return (
        <div
          className="w-6 h-4 bg-red-700"
          onClick={() => this.setOnOff(true)}
        />
      )
    }
  }

  renderModal = () => {
    const { showFixedTypeModal } = this.state
    const { children, modalType, renderChildren } = this.props
    const modalStyle = this.generateStyle()
    if (modalType === ModalTypes.fixed) {
      return (
        <div
          ref={(ref) => {
            if (!this.isRefValid(this.modalRef)) {
              this.modalRef = ref
              this.tryFlushModal()
            }
          }}
          className={`fixed z-10 ${
            showFixedTypeModal ? 'opacity-100' : 'opacity-0'
          }`}
          style={modalStyle}
        >
          {renderChildren && renderChildren({ setOnOff: this.setOnOff })}
          {children}
        </div>
      )
    } else {
      return (
        <div
          ref={(ref) => {
            if (!this.isRefValid(this.modalRef)) {
              this.modalRef = ref
              this.tryFlushModal()
            }
          }}
          className="absolute z-10"
          style={modalStyle}
        >
          {renderChildren && renderChildren({ setOnOff: this.setOnOff })}
          {children}
        </div>
      )
    }
  }

  renderHangingPosition = () => {
    const isOpen = this.getIsOpen()

    return (
      <div
        ref={(ref) => {
          if (!this.isRefValid(this.containerRef)) {
            this.containerRef = ref
            this.tryFlushModal()
          }
        }}
      >
        {isOpen && (
          <ModalWrapper
            className="relative"
            onClickOutside={this.handleClickOutside}
          >
            {this.renderModal()}
          </ModalWrapper>
        )}
      </div>
    )
  }

  render() {
    const { className, anchorPosition } = this.props

    // FIXME: There's got to be a better way of doing this.
    return (
      <div className={className}>
        <div className="flex flex-row justify-between w-full">
          {anchorPosition === AnchorPositions.topLeft &&
            this.renderHangingPosition(AnchorPositions.topLeft)}
          {anchorPosition === AnchorPositions.topCenter &&
            this.renderHangingPosition(AnchorPositions.topCenter)}
          {anchorPosition === AnchorPositions.topRight &&
            this.renderHangingPosition(AnchorPositions.topRight)}
        </div>
        <div
          ref={this.setTriggerRef}
          className="flex flex-row justify-between items-center"
        >
          {anchorPosition === AnchorPositions.leftCenter &&
            this.renderHangingPosition(AnchorPositions.leftCenter)}
          {this.renderTriggerContent()}
          {anchorPosition === AnchorPositions.rightCenter &&
            this.renderHangingPosition(AnchorPositions.rightCenter)}
        </div>
        <div className="flex flex-row justify-between w-full">
          {anchorPosition === AnchorPositions.bottomLeft &&
            this.renderHangingPosition(AnchorPositions.bottomLeft)}
          {anchorPosition === AnchorPositions.bottomCenter &&
            this.renderHangingPosition(AnchorPositions.bottomCenter)}
          {anchorPosition === AnchorPositions.bottomRight &&
            this.renderHangingPosition(AnchorPositions.bottomRight)}
        </div>
      </div>
    )
  }
}
